/**
 * Annuler et refaire d'un brouillon d'éditeur : état pur, sans effet, limité en profondeur. Une suite
 * de frappes sur la même propriété (`coalesce`) ne crée qu'une étape, comme un éditeur de texte.
 */

export interface EditorHistory<T> {
  past: T[];
  present: T;
  future: T[];
  /** Clé de la dernière étape, pour regrouper des modifications successives du même champ. */
  lastKey?: string;
}

export const HISTORY_LIMIT = 80;

export function startHistory<T>(present: T): EditorHistory<T> {
  return { past: [], present, future: [] };
}

export function commit<T>(history: EditorHistory<T>, next: T, coalesce?: string): EditorHistory<T> {
  if (Object.is(next, history.present)) return history;
  if (coalesce !== undefined && coalesce === history.lastKey && history.past.length > 0) return { past: history.past, present: next, future: [], lastKey: coalesce };
  const past = [...history.past, history.present].slice(-HISTORY_LIMIT);
  return coalesce === undefined ? { past, present: next, future: [] } : { past, present: next, future: [], lastKey: coalesce };
}

export function undo<T>(history: EditorHistory<T>): EditorHistory<T> {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1]!;
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future] };
}

export function redo<T>(history: EditorHistory<T>): EditorHistory<T> {
  if (history.future.length === 0) return history;
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present].slice(-HISTORY_LIMIT), present: next!, future: rest };
}

export const canUndo = (history: EditorHistory<unknown>): boolean => history.past.length > 0;
export const canRedo = (history: EditorHistory<unknown>): boolean => history.future.length > 0;
